import { forwardRef, Fragment } from "react";
import { useLocation } from "react-router-dom";
import { mapHistoryRows } from "src/components/pages/Reports/Report/Section.utils";
import PreTextCell from "src/generator/_PreText/PreTextSection/PreTextCell";
import { type ICol, type ISection } from "src/interfaces";
import tableStyles from "src/styled/sharedTable.module.scss";

import Header from "../../Header";
import SectionTitle from "../SectionTitle";
import Row from "./Row";

interface Props {
  section: ISection;
  historyRows?: ICol[][][];
  deletedRows?: Array<{
    data: {
      sectionIndex: number;
      deletedRowIndex: number;
      rows: ICol[][];
      [key: string]: any;
    };
    [key: string]: any;
  }>;
  sIndex: number;
  applyHistory?: boolean;
  horizontalScroll?: boolean;
  isOverlay?: boolean;
  titleComponent?: JSX.Element;
  lbeid?: string;
  entryIndex?: number;
  offset?: number;
  readOnly?: boolean;
  snapshot?: boolean;
  pdfEntry?: boolean;
  fullRows?: boolean;
  hideHeaders?: boolean;
  layout?: number[];
  smallFont?: boolean;
}

const Section = forwardRef<HTMLDivElement, Props>(
  (
    {
      section,
      historyRows,
      deletedRows = [],
      sIndex,
      applyHistory,
      horizontalScroll,
      isOverlay,
      titleComponent,
      lbeid,
      entryIndex,
      offset,
      readOnly,
      snapshot,
      pdfEntry,
      fullRows,
      hideHeaders,
      layout,
      smallFont,
    }: Props,
    ref,
  ) => {
    const location = useLocation();
    const isInLogbookEntry =
      location.pathname.includes("/logbook/") &&
      !location.pathname.includes("/history");

    const isExpanded =
      !isInLogbookEntry || !section.collapseable || !section.collapsed;
    const shouldHorizontallyScroll = horizontalScroll && isExpanded;
    const overlayControlledClassName = isOverlay
      ? "overlay"
      : `${section.className}_scroll`;

    const hrows = historyRows
      ? mapHistoryRows(historyRows, section, deletedRows as any)
      : [];
    const renderRows = () => {
      if (!section?.rows || !Array.isArray(section.rows)) return [];
      const isSubRows =
        // @ts-expect-error TS7053: Element implicitly has an any type because expression of type 0 can't be used to index type
        section.rows[0]?.[0] && Array.isArray(section.rows[0][0]);

      // Calculate max rows to include deleted rows from history
      const maxRows = Math.max(section.rows.length, hrows.length);

      return Array.from({ length: maxRows }, (_, index) => {
        const row = section.rows[index]; // May be undefined for deleted rows
        const hasCurrentRow = index < section.rows.length;

        const previousRow = index > 0 ? section.rows[index - 1] : null;
        const addBorder =
          snapshot && previousRow
            ? previousRow?.entrydate !== row?.entrydate
            : false;
        return (
          <Fragment key={`section-row-${index}`}>
            {hrows[index] &&
              Array.isArray(hrows[index]) &&
              hrows?.[index].map((x: any, historyIndex: number) => {
                const isLastItem = historyIndex === hrows[index].length - 1;
                // If this is beyond the current rows, it's a deleted row
                const isDeletedRow = index >= section.rows.length;
                const isHistoryRow = isDeletedRow || !isLastItem; // Mark as history if deleted or not the last item

                if (Array.isArray(x[0])) {
                  return x.map((subrow: any, i: number) => (
                    <Row
                      key={`history-${historyIndex}-${i}`}
                      row={subrow}
                      history={isHistoryRow}
                      isOverlay={isOverlay}
                      lbeid={lbeid}
                      showSubRowHeaders={section.showSubRowHeaders}
                      headers={section.headers}
                      entryIndex={entryIndex}
                      divider={addBorder}
                      pdfEntry={pdfEntry}
                      layout={layout}
                    />
                  ));
                }
                return (
                  <Row
                    key={`history-${historyIndex}`}
                    row={x}
                    history={isHistoryRow}
                    isOverlay={isOverlay}
                    lbeid={lbeid}
                    showSubRowHeaders={section.showSubRowHeaders}
                    headers={section.headers}
                    entryIndex={entryIndex}
                    divider={addBorder}
                    pdfEntry={pdfEntry}
                    layout={layout}
                  />
                );
              })}
            {(hasCurrentRow && hrows[index]?.length === 0) ||
              (hasCurrentRow && hrows.length === 0 && (
                <>
                  {isSubRows ? (
                    (row as unknown as ICol[]).map((subrow: any, i: number) => (
                      <Row
                        key={`current-row-${i}`}
                        row={subrow}
                        subRowIndex={i}
                        rowIndex={index}
                        rowsLength={section.rows.length}
                        history={applyHistory}
                        isOverlay={isOverlay}
                        lbeid={lbeid}
                        showSubRowHeaders={section.showSubRowHeaders}
                        headers={section.headers}
                        entryIndex={entryIndex}
                        divider={addBorder}
                        pdfEntry={pdfEntry}
                        layout={layout}
                      />
                    ))
                  ) : (
                    <Row
                      key={`current-row-${index}`}
                      rowIndex={index}
                      rowsLength={section.rows.length}
                      row={row as any}
                      history={applyHistory}
                      isOverlay={isOverlay}
                      lbeid={lbeid}
                      showSubRowHeaders={section.showSubRowHeaders}
                      headers={section.headers}
                      entryIndex={entryIndex}
                      divider={addBorder}
                      pdfEntry={pdfEntry}
                      layout={layout}
                    />
                  )}
                </>
              ))}
          </Fragment>
        );
      });
    };

    return (
      <div
        style={{
          pageBreakInside: "avoid",
          width: "100%",
          marginBottom: !fullRows ? "20px" : undefined,
          marginTop: fullRows ? (sIndex !== 0 ? "-1px" : undefined) : undefined,
        }}>
        {titleComponent}
        <div
          ref={ref}
          className={`${overlayControlledClassName}${
            shouldHorizontallyScroll ? ` ${tableStyles.sectionTableScroll}` : ""
          }`}
          style={{ width: "100%" }}>
          {isExpanded &&
            section.pretext &&
            pdfEntry &&
            section.pretext
              .filter((item: any) => item.type !== "title")
              .map((item: any, i: number) => (
                <div
                  key={`pretext-${i}`}
                  style={{
                    padding: "10px",
                    border: "1px solid black",
                    borderBottom: "none",
                  }}>
                  <PreTextCell pretext={{ ...item, border: false }} />
                </div>
              ))}
          <table
            key={Math.random()}
            style={{
              width: "100%",
              minWidth: horizontalScroll ? "100%" : undefined,
              tableLayout: layout ? "fixed" : "auto",
              borderSpacing: 0,
              borderCollapse: "collapse",
              pageBreakInside: "avoid",
              fontFamily: "Arial, sans-serif",
              fontSize: smallFont ? "9pt" : "0.875rem",
              borderTop: section.pretext ? "1px solid black" : undefined,
            }}>
            {layout && layout.length > 0 && (
              <colgroup>
                {layout.map((ratio, index) => {
                  const totalRatio = layout.reduce(
                    (sum, value) => sum + value,
                    0,
                  );
                  const width =
                    totalRatio > 0
                      ? `${(ratio / totalRatio) * 100}%`
                      : undefined;

                  return <col key={`layout-col-${index}`} style={{ width }} />;
                })}
              </colgroup>
            )}
            {!titleComponent && (
              <SectionTitle
                {...section}
                sectionIndex={sIndex}
                collapseable={section.collapseable}
                collapsed={section.collapsed}
                asTableHeader
              />
            )}
            {isExpanded && (
              <>
                {!section.showSubRowHeaders && (
                  <Header
                    headers={
                      section?.headers &&
                      section.headers &&
                      Array.isArray(section?.headers[0])
                        ? section.headers[0]
                        : section.headers
                    }
                    sIndex={sIndex}
                    headerSize={section.headerSize}
                    entryIndex={entryIndex}
                    offset={offset}
                    pdfEntry={pdfEntry}
                    hideHeaders={hideHeaders}
                    layout={layout}
                  />
                )}
                <tbody>{renderRows().flat()}</tbody>
              </>
            )}
          </table>
        </div>
      </div>
    );
  },
);

export default Section;
